import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ErrorHandler } from '../utils/errorHandler';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://aircons-inventory-backend.onrender.com/api',
  timeout: 60000, // Increased timeout for AWS cold starts
  headers: {
    'Content-Type': 'application/json',
  },
  // Performance optimizations for AWS
  maxRedirects: 5,
  maxContentLength: 50 * 1024 * 1024, // 50MB
  validateStatus: (status) => status < 500, // Don't reject on 4xx errors
});

// Simple cache for frequently accessed data
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes (increased for better performance)

// Request batching for multiple simultaneous requests
const pendingRequests = new Map<string, Promise<any>>();

// Request interceptor to add auth token and performance optimizations
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Performance optimizations for AWS
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
    
    // Add request timestamp for debugging
    (config as any).metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and performance monitoring
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response time for performance monitoring
    const startTime = (response.config as any).metadata?.startTime;
    if (startTime) {
      const duration = new Date().getTime() - startTime.getTime();
      if (duration > 2000) { // Log slow requests (>2 seconds)
      }
    }
    
    // Cache successful GET responses (but not for products with timestamp)
    if (response.config.method === 'get' && 
        !response.config.url?.includes('auth') && 
        !response.config.url?.includes('_t=')) {
      const cacheKey = `${response.config.url}`;
      cache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
    }
    
    return response;
  },
  (error: AxiosError) => {
    // Log error response time
    const startTime = (error.config as any)?.metadata?.startTime;
    if (startTime) {
      const duration = new Date().getTime() - startTime.getTime();
    }
    
    // On 401, do not clear auth here to avoid unintended logouts during soft refreshes.
    // Let higher-level auth checks decide how to handle expired/invalid tokens.
    // if (error.response?.status === 401) {
    //   // Token expired or invalid
    //   localStorage.removeItem('token');
    //   localStorage.removeItem('user');
    // }
    
    // Handle rate limiting (429 errors)
    if (error.response?.status === 429) {
      // Don't reject immediately, let the retry logic handle it
    }
    
    return Promise.reject(error);
  }
);

// API Service class
class ApiService {
  // Retry configuration
  private maxRetries = 3;
  private retryDelay = 1000; // Start with 1 second

  // Retry mechanism with exponential backoff
  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    retryCount = 0
  ): Promise<AxiosResponse<T>> {
    try {
      return await requestFn();
    } catch (error: any) {
      if (error.response?.status === 429 && retryCount < this.maxRetries) {
        const delay = this.retryDelay * Math.pow(2, retryCount); // Exponential backoff
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.retryRequest(requestFn, retryCount + 1);
      }
      throw error;
    }
  }

  // Check cache for GET requests
  private getFromCache<T>(url: string): T | null {
    const cacheKey = url;
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  // Generic HTTP methods
  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    // Check if we need to bypass cache (force refresh)
    const forceRefresh = config?.forceRefresh || false;
    
    // For products endpoint, always bypass cache if force refresh is requested
    if (url.includes('/products') && forceRefresh) {
      return this.retryRequest(() => api.get<T>(url, config));
    }
    
    // Check cache first for non-auth requests (unless force refresh is requested)
    if (!url.includes('auth') && !forceRefresh) {
      const cachedData = this.getFromCache<T>(url);
      if (cachedData) {
        return Promise.resolve({
          data: cachedData,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: { url } as any,
        });
      }
    }
    
    // Check if there's already a pending request for this URL
    if (pendingRequests.has(url)) {
      const pendingResponse = await pendingRequests.get(url);
      return pendingResponse;
    }
    
    // Create new request with retry logic and store it
    const requestPromise = this.retryRequest(() => api.get<T>(url, config));
    pendingRequests.set(url, requestPromise);
    
    try {
      const response = await requestPromise;
      return response;
    } finally {
      // Clean up pending request
      pendingRequests.delete(url);
    }
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    const response = await this.retryRequest(() => api.post<T>(url, data, config));
    // Invalidate related caches after POST operations
    this.invalidateRelatedCaches(url);
    return response;
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    const response = await this.retryRequest(() => api.put<T>(url, data, config));
    // Invalidate related caches after PUT operations
    this.invalidateRelatedCaches(url);
    return response;
  }

  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    const response = await this.retryRequest(() => api.delete<T>(url, config));
    // Invalidate related caches after DELETE operations
    this.invalidateRelatedCaches(url);
    return response;
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    const response = await this.retryRequest(() => api.patch<T>(url, data, config));
    // Invalidate related caches after PATCH operations
    this.invalidateRelatedCaches(url);
    return response;
  }

  // File upload
  async upload<T = any>(url: string, formData: FormData, config?: any): Promise<AxiosResponse<T>> {
    return api.post<T>(url, formData, {
      ...config,
      timeout: 0, // No timeout for file uploads
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    });
  }

  // Download file
  async download(url: string, filename?: string): Promise<void> {
    const response = await api.get(url, {
      responseType: 'blob',
    });
    
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Excel import with no timeout
  async importExcel<T = any>(url: string, formData: FormData, config?: any): Promise<AxiosResponse<T>> {
    return api.post<T>(url, formData, {
      ...config,
      timeout: 0, // No timeout for Excel imports
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    });
  }



  // Send invoice email
  async sendInvoiceEmail({ to, invoiceData, invoiceType = 'invoice' }: { to: string; invoiceData: any; invoiceType?: string; }) {
    return api.post('/email/invoice', { to, invoiceData, invoiceType });
  }

  // Batch multiple requests for better performance
  async batchGet<T = any>(urls: string[]): Promise<AxiosResponse<T>[]> {
    const requests = urls.map(url => this.get<T>(url));
    return Promise.all(requests);
  }

  // Preload critical data
  async preloadCriticalData() {
    const criticalEndpoints = [
      '/products',
      '/categories/all',
      '/brands/all',
      '/product-types/all',
      '/compressor-types/all',
      '/countries/all',
      '/units/all',
      '/locations/all'
    ];
    
    await this.batchGet(criticalEndpoints);
  }

  // Clear cache
  clearCache(url?: string) {
    if (url) {
      cache.delete(url);
    } else {
      cache.clear();
    }
  }

  // Invalidate related caches based on URL patterns
  private invalidateRelatedCaches(url: string) {
    const cacheKeys = Array.from(cache.keys());
    const clearedKeys: string[] = [];
    
    // Clear specific endpoint cache
    cache.delete(url);
    clearedKeys.push(url);
    
    // Clear related list caches based on URL patterns
    if (url.includes('/products')) {
      // Clear products list cache
      cacheKeys.forEach(key => {
        if (key.includes('/products') && !key.includes('/products/')) {
          cache.delete(key);
          clearedKeys.push(key);
        }
      });
    } else if (url.includes('/invoices')) {
      // Clear invoices list cache
      cacheKeys.forEach(key => {
        if (key.includes('/invoices') && !key.includes('/invoices/')) {
          cache.delete(key);
          clearedKeys.push(key);
        }
      });
    } else if (url.includes('/customers')) {
      // Clear customers list cache
      cacheKeys.forEach(key => {
        if (key.includes('/customers') && !key.includes('/customers/')) {
          cache.delete(key);
          clearedKeys.push(key);
        }
      });
    } else if (url.includes('/suppliers')) {
      // Clear suppliers list cache
      cacheKeys.forEach(key => {
        if (key.includes('/suppliers') && !key.includes('/suppliers/')) {
          cache.delete(key);
          clearedKeys.push(key);
        }
      });
    } else if (url.includes('/projects')) {
      // Clear projects list cache
      cacheKeys.forEach(key => {
        if (key.includes('/projects') && !key.includes('/projects/')) {
          cache.delete(key);
          clearedKeys.push(key);
        }
      });
    } else if (url.includes('/requisitions')) {
      cacheKeys.forEach(key => {
        if (key.includes('/requisitions')) {
          cache.delete(key);
          clearedKeys.push(key);
        }
      });
    } else if (url.includes('/categories') || url.includes('/brands') || url.includes('/types') || url.includes('/locations') || url.includes('/units') || url.includes('/countries')) {
      // Clear all related master data caches
      cacheKeys.forEach(key => {
        if (key.includes('/categories') || key.includes('/brands') || key.includes('/types') || key.includes('/locations') || key.includes('/units') || key.includes('/countries')) {
          cache.delete(key);
          clearedKeys.push(key);
        }
      });
    }
    
  }

  // Get cache stats
  getCacheStats() {
    return {
      size: cache.size,
      entries: Array.from(cache.keys())
    };
  }

  // Force refresh data (bypass cache)
  async forceRefresh<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    // Clear cache for this specific URL
    cache.delete(url);
    return this.get<T>(url, { ...config, forceRefresh: true });
  }

  // Invalidate specific cache entries
  invalidateCache(urls: string[]) {
    urls.forEach(url => cache.delete(url));
  }

  // Invalidate products cache specifically
  invalidateProductsCache() {
    const cacheKeys = Array.from(cache.keys());
    const productsKeys = cacheKeys.filter(key => key.includes('/products'));
    productsKeys.forEach(key => cache.delete(key));
  }

  // Invalidate invoices cache specifically
  invalidateInvoicesCache() {
    const cacheKeys = Array.from(cache.keys());
    const invoicesKeys = cacheKeys.filter(key => key.includes('/invoices'));
    invoicesKeys.forEach(key => cache.delete(key));
  }

  // Invalidate customers cache specifically
  invalidateCustomersCache() {
    const cacheKeys = Array.from(cache.keys());
    const customersKeys = cacheKeys.filter(key => key.includes('/customers'));
    customersKeys.forEach(key => cache.delete(key));
  }

  // Invalidate suppliers cache specifically
  invalidateSuppliersCache() {
    const cacheKeys = Array.from(cache.keys());
    const suppliersKeys = cacheKeys.filter(key => key.includes('/suppliers'));
    suppliersKeys.forEach(key => cache.delete(key));
  }

  // Invalidate projects cache specifically
  invalidateProjectsCache() {
    const cacheKeys = Array.from(cache.keys());
    const projectsKeys = cacheKeys.filter(key => key.includes('/projects'));
    projectsKeys.forEach(key => cache.delete(key));
  }

  // Invalidate users cache specifically
  invalidateUsersCache() {
    const cacheKeys = Array.from(cache.keys());
    const usersKeys = cacheKeys.filter(key => key.includes('/users'));
    usersKeys.forEach(key => cache.delete(key));
  }

  // Invalidate master data cache specifically
  invalidateMasterDataCache() {
    const cacheKeys = Array.from(cache.keys());
    const masterDataKeys = cacheKeys.filter(key => 
      key.includes('/categories') || 
      key.includes('/brands') || 
      key.includes('/types') || 
      key.includes('/locations') || 
      key.includes('/units') || 
      key.includes('/countries') ||
      key.includes('/compressor-types')
    );
    masterDataKeys.forEach(key => cache.delete(key));
  }

  // Invalidate warranties cache specifically
  invalidateWarrantiesCache() {
    const cacheKeys = Array.from(cache.keys());
    const warrantiesKeys = cacheKeys.filter(key => key.includes('/warranties'));
    warrantiesKeys.forEach(key => cache.delete(key));
  }

  // Invalidate requisitions cache specifically
  invalidateRequisitionsCache() {
    const cacheKeys = Array.from(cache.keys());
    const requisitionsKeys = cacheKeys.filter(key => key.includes('/requisitions'));
    requisitionsKeys.forEach(key => cache.delete(key));
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Export axios instance for direct use if needed
export { api };

// Export types
export type { AxiosResponse, AxiosError }; 